import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { PasswordInput } from '../components/ui/password-input';
import logoImage from '@/assets/prsnl-logo.svg';
import { toast } from 'sonner';

import { updatePassword, onAuthStateChange, getSession, verifyRecoveryToken } from '@/services/data/auth';
import { updateHasSignedUp } from '@/services/data/users';
import { PageLoader } from '@/components/ui/page-loader';
import { useSupabaseVerificationLink } from '@/hooks/useSupabaseVerificationLink';
import { useAuthStore } from '@/stores/authStore';

export function SetPasswordScreen() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const [userId, setUserId] = useState<string | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const { tokenHash, type: verificationType } = useSupabaseVerificationLink();
  const hasVerifiedRef = useRef(false);

  // Wait for auth session to be established
  useEffect(() => {
    let isMounted = true;

    const markAuthenticated = (userIdValue: string) => {
      if (!isMounted) return;
      setUserId(userIdValue);
      setVerificationError(null);
      setIsCheckingAuth(false);
    };

    const verifyAndFetchSession = async () => {
      if (tokenHash && verificationType === 'recovery' && !hasVerifiedRef.current) {
        hasVerifiedRef.current = true;
        const { success, data } = await verifyRecoveryToken(tokenHash);

        if (!isMounted) return;

        if (success && data?.user) {
          markAuthenticated(data.user.id);
          window.history.replaceState({}, '', '/set-password');
          return;
        }
      }

      const result = await getSession();
      if (!isMounted) return;

      if (result.success && result.data?.user) {
        markAuthenticated(result.data.user.id);
      }
    };

    verifyAndFetchSession();

    const { data: { subscription } } = onAuthStateChange((event, session) => {
      if (!isMounted) return;
      
      if (session?.user) {
        markAuthenticated(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        navigate('/login');
      }
    });


    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, tokenHash, verificationType]);

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères';
    }
    if (!/[a-z]/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins une minuscule';
    }
    if (!/[A-Z]/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins une majuscule';
    }
    if (!/[0-9]/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins un chiffre';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if user is authenticated
    if (!userId) {
      setError('Aucun utilisateur connecté. Veuillez réessayer de cliquer sur le lien dans votre email.');
      return;
    }

    // Validation
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      // Update user password
      const updateResult = await updatePassword(password);

      if (!updateResult.success) {
        setError(updateResult.error?.message || 'Erreur lors de la création du mot de passe');
        setIsLoading(false);
        return;
      }

      // Update user table to mark as signed up
      const profileResult = await updateHasSignedUp(userId, true);

      if (!profileResult.success) {
        // Don't block the user if this fails
      }
      
      // Sync session to authStore before navigating
      const sessionResult = await getSession();
      if (sessionResult.success && sessionResult.data) {
        const { setSession } = useAuthStore.getState();
        setSession(sessionResult.data);
      }
      
      toast.success('Mot de passe créé avec succès !');
      await new Promise(resolve => setTimeout(resolve, 100));
      globalThis.location.replace(`${globalThis.location.origin}/login`);

    } catch (error) {
      console.error('Password update error:', error);
      setError('Une erreur inattendue est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return <PageLoader variant="screen" message="Vérification de la session..." />;
  }

  // Show error state if verification failed
  if (verificationError) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-slow-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />
        
        <div className="text-center w-full max-w-md mx-auto relative z-10">
          <img 
            src={logoImage} 
            alt="PRSNL" 
            className="w-40 h-auto mx-auto mb-8 brightness-0 invert"
          />
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-white mb-2">
              Lien invalide
            </h1>
            <p className="text-white/60 text-sm mb-6">
              {verificationError}
            </p>
            <Button
              asChild
              className="w-full h-12 rounded-full bg-white text-black hover:bg-white/90 font-medium transition-all"
            >
              <Link to="/login">Retour à la connexion</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-between p-6 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-warning/10 rounded-full blur-3xl animate-slow-pulse" style={{ animationDelay: '5s' }} />

      {/* Status bar spacer */}
      <div className="h-12" />

      {/* Logo and content centered */}
      <div className="flex-1 flex items-center justify-center relative z-10 w-full">
        <div className="text-center w-full max-w-md mx-auto">
          <img 
            src={logoImage} 
            alt="PRSNL" 
            className="w-40 h-auto mx-auto mb-8 brightness-0 invert"
          />
          
          <h1 className="text-2xl font-bold text-white mb-2">Créer votre mot de passe</h1>
          <p className="text-white/60 mb-8">Sécurisez votre compte avec un mot de passe</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-3xl p-4">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}
            
            <div className="space-y-3 text-left">
              <label htmlFor="password" className="text-white text-sm block mb-2">
                Mot de passe
              </label>
              <PasswordInput
                id="password"
                placeholder="Minimum 8 caractères"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-3xl h-14 px-6"
                required
              />
            </div>

            <div className="space-y-3 text-left">
              <label htmlFor="confirmPassword" className="text-white text-sm block mb-2">
                Confirmer le mot de passe
              </label>
              <PasswordInput
                id="confirmPassword"
                placeholder="Retapez votre mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-3xl h-14 px-6"
                required
              />
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-3xl p-4">
              <p className="text-xs text-white/60 text-left">
                Le mot de passe doit contenir :
              </p>
              <ul className="text-xs text-white/60 list-disc list-inside text-left mt-2 space-y-1">
                <li>Au moins 8 caractères</li>
                <li>Une majuscule et une minuscule</li>
                <li>Au moins un chiffre</li>
              </ul>
            </div>

            <Button 
              type="submit"
              className="w-full h-14 rounded-3xl text-base font-medium bg-white text-black hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !userId}
            >
              {isLoading ? 'Création...' : 'Créer mon mot de passe'}
            </Button>
          </form>
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="h-12" />
    </div>
  );
}
